import cx from 'classnames';

import type { Post } from '~/interfaces/post';
import { BORDER_RADIUS_OPTIONS } from '~/utils/designOptions';

import PostAuthors from './PostAuthors';
import PostDetails from './PostDetails';
import PostImage from './PostImage';
import PostInsights from './PostInsights';
import PostLinkHelper from './PostLinkHelper';
import PostPremiumBadge from './PostPremiumBadge';
import PostTags from './PostTags';
import PostTitles from './PostTitles';
import type { PostCardStyles } from './types';

interface Props {
  post: Post;
  cardStyles: PostCardStyles;
  orientation?: 'vertical' | 'horizontal';
}

// Note the PostLinkHelper: there are a number of different links in this component and it is
// invalid html to have an anchor tag inside another anchor tag.

const PostCardv3 = ({ post, cardStyles, orientation = 'vertical' }: Props) => {
  const isPremium = post.audience === 'premium';
  const radiusClass = BORDER_RADIUS_OPTIONS[cardStyles?.border_radius || 'none'];
  const isVertical = orientation === 'vertical';
  const verticalClass = 'flex flex-col';
  const isHorizontal = orientation === 'horizontal';
  const horizontalClass = 'flex flex-col md:flex-row md:items-center items-start max-w-4xl';

  return (
    <div
      suppressHydrationWarning
      className={cx(
        'transparent h-full cursor-pointer overflow-hidden',
        radiusClass,
        isHorizontal && horizontalClass,
        isVertical && verticalClass,
        cardStyles?.border_color && 'border',
      )}
      style={{
        backgroundColor: cardStyles?.bg_color,
        borderColor: cardStyles?.border_color,
      }}
    >
      <div className={cx('relative w-full', isHorizontal && 'max-w-sm')}>
        {cardStyles?.tags_enabled && (
          <PostTags tags={post.content_tags} cardStyles={cardStyles} className="absolute left-4 top-4 z-10" />
        )}
        <PostLinkHelper slug={post.slug}>
          <PostImage
            alt={post.web_title}
            radiusClass={radiusClass}
            hasRadiusApplied={cardStyles?.is_image_radius_applied}
            src={post.image_url}
          />
        </PostLinkHelper>
      </div>

      <div className="w-full p-3">
        <div className="space-y-3">
          <PostLinkHelper slug={post.slug}>
            <div className="space-y-2">
              <div className="flex flex-wrap-reverse items-center justify-between">
                {cardStyles.timefields_enabled && (
                  <PostDetails
                    fontFamily={cardStyles.font}
                    color={cardStyles.timestamp_text_color}
                    timestamp={post.override_scheduled_at}
                  />
                )}
                {cardStyles.likes_comments_enabled && (
                  <PostInsights post={post} color={cardStyles.likes_comments_color} />
                )}
              </div>
              {isPremium && (
                <PostPremiumBadge
                  fontFamily={cardStyles.font}
                  bgColor={cardStyles.premium_bg_color}
                  color={cardStyles.premium_text_color}
                />
              )}
              <PostTitles
                title={{
                  size: isHorizontal ? 'lg' : 'xl',
                  text: post.web_title,
                  fontFamily: cardStyles.heading_font_family,
                  color: cardStyles.heading_font_color,
                }}
                subtitle={{
                  size: isHorizontal ? 'sm' : 'md',
                  text: post?.web_subtitle || '',
                  fontFamily: cardStyles.description_font_family,
                  color: cardStyles.description_font_color,
                }}
              />
            </div>
          </PostLinkHelper>

          <PostAuthors
            authors={post.authors}
            color={cardStyles.authors_text_color}
            bgColor={cardStyles.bg_color}
            font={cardStyles.font}
            showAuthorImage={cardStyles.show_author_image}
          />
        </div>
      </div>
    </div>
  );
};

export default PostCardv3;
